import React, { useState, useEffect,useMemo } from 'react';
import { api } from '../config/api';
import { Layers, Plus, Search, Edit, Trash2, Save, Loader2, List } from 'lucide-react';
import toast from '../utils/toast';
import SearchBar from '../components/SearchBar';
import {
  masterAccentButton,
  masterHeader,
  masterHeaderCard,
  masterHeaderIcon,
  masterHeaderInfo,
  masterInput,
  masterInputLabel,
  masterPageShell,
  masterPanel,
  masterSearchInput,
  masterSearchWrap,
  masterStickyHeadRow,
  masterToolbar,
  masterSubtitle,
  masterTitle,
  masterTableData,
  masterTableID
} from '../components/masterPageStyles';

const DepartmentList = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('form'); // Default to form page view
  const [isSaving, setIsSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(18);
  const [formData, setFormData] = useState({ deptName: '', status: 'Active' });

  const fetchDepartments = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/master/department/all`);
      if (res.data.success) setDepartments(res.data.data);
    } catch (err) { 
      toast.error("Failed to load departments"); 
    } finally { 
      setLoading(false); 
    }
  };

  useEffect(() => { 
    fetchDepartments(); 
  }, []);

  // ─── SORT DESCENDING & PREFIX SEARCH ───
  const filteredList = useMemo(() => {
    const sortedList = [...departments].sort((a, b) => {
      const idA = String(a.deptId || a._id || '');
      const idB = String(b.deptId || b._id || '');
      return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedList;

    return sortedList.filter((item) => {
      const name = (item.deptName || '').toLowerCase();
      const deptId = (item.deptId || '').toLowerCase();
      return name.startsWith(q) || deptId.startsWith(q);
    });
  }, [departments, searchTerm]);

  const paginatedList = useMemo(() => {
    if (pageSize === -1) return filteredList;
    const start = (currentPage - 1) * pageSize;
    return filteredList.slice(start, start + pageSize);
  }, [filteredList, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  const handleAddNew = () => {
    setEditId(null);
    setFormData({ deptName: '', status: 'Active' });
    setViewMode('form');
  };

  const handleEdit = (dept) => {
    setEditId(dept._id);
    setFormData({ deptName: dept.deptName, status: dept.status || 'Active' });
    setViewMode('form');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const payload = editId
        ? { id: editId, deptName: formData.deptName.trim().toUpperCase(), status: formData.status }
        : { deptName: formData.deptName.trim().toUpperCase(), status: formData.status };
        
      const res = await api.post(`/master/department/upsert`, payload);
      if (res.data.success) {
        toast.success(editId ? "Updated" : "Saved");
        setFormData({ deptName: '', status: 'Active' });
        setEditId(null);
        fetchDepartments();
        setViewMode('form'); // Keeps you on the default form view after saving
      }
    } catch (err) { 
      toast.error(err.response?.data?.message || "Error"); 
    } finally { 
      setIsSaving(false); 
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this department?")) {
      try {
        await api.delete(`/master/department/${id}`);
        toast.success("Deleted");
        fetchDepartments();
      } catch (err) { 
        toast.error("Delete failed"); 
      }
    }
  };

  return (
    <div className={masterPageShell}>
      <header className={masterHeader}>
        <div className={masterHeaderCard}>
          <div className={masterHeaderInfo}>
            <div className={masterHeaderIcon}>
              <Layers size={18} />
            </div>
            <div>
              <h1 className={masterTitle}>Department Master</h1>
              <p className={masterSubtitle}>Hospital Unit Management</p>
            </div>
          </div>
          <div>
            {viewMode === 'form' ? (
              <button onClick={() => setViewMode('list')} className={masterAccentButton}>
                <List size={14} /> View List
              </button>
            ) : (
              <button onClick={handleAddNew} className={masterAccentButton}>
                <Plus size={14} /> Add Department
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="px-2 pb-2">
        <div className="max-w-7xl mx-auto space-y-2">
          
          {/* VIEW 1: FULL WIDTH GRID FORM (NO ID FIELD) */}
          {viewMode === 'form' && (
            <div className="p-1">
              {/* Changed max-w-2xl to max-w-full for true full width panel look */}
              <form onSubmit={handleSubmit} className="bg-white w-full max-w-full rounded-xl border border-slate-300 px-2 py-2 shadow-md">
                <div className="mb-1">
                  <h2 className="text-sm font-bold text-slate-800 uppercase tracking-tight">
                    {editId ? 'Edit Dept Details' : 'Create New Dept'}
                  </h2>
                </div>
                
                {/* 2-Column Responsive Form Layout */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className={masterInputLabel}>Department Name</label>
                    <input 
                      required 
                      type="text" 
                      value={formData.deptName} 
                      autoFocus
                      onChange={(e) => setFormData({...formData, deptName: e.target.value})}
                      placeholder="E.G. RADIOLOGY"
                      className={masterInput}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className={masterInputLabel}>Status</label>
                    <select 
                      value={formData.status} 
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      className={masterInput}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="mt-1 flex justify-end">
                  <button disabled={isSaving} className={`${masterAccentButton} w-full md:w-auto px-1 py-1 justify-center`}>
                    {isSaving ? <Loader2 className="animate-spin" size={20}/> : <Save size={20}/>}
                    {editId ? 'UPDATE CHANGES' : 'SAVE DEPARTMENT'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* VIEW 2: LIST ARCHITECTURE (SHOWS GENERATED DEPT ID) */}
          {viewMode === 'list' && (
            <div className={masterPanel}>
             <SearchBar
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="Search department..."
                    totalItems={filteredList.length}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={setPageSize}
                  />
              <div className="overflow-auto">
                <table className="w-full min-w-[560px] table-fixed text-left">
                  <thead>
                    <tr className={masterStickyHeadRow}>
                      <th className={masterTableData}>S.No</th>
                      <th className={masterTableData}>Dept ID</th>
                      <th className={masterTableData}>Department Name</th>
                      <th className={masterTableData}>Status</th>
                      <th className={masterTableData}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-300 font-bold text-slate-600">
                    {loading ? (
                      <tr>
                        <td colSpan="5" className="p-12 text-center">Syncing...</td>
                      </tr>
                    ) : paginatedList.length > 0 ? (
                        paginatedList.map((dept, index) => (
                        <tr key={dept._id} className="hover:bg-emerald-50/30">
                          <td className={masterTableData}>
                            {pageSize === -1 ? index + 1 : (currentPage - 1) * pageSize + index + 1}
                          </td>
                          <td className={masterTableID}>
                            {dept.deptId}                          
                          </td>
                          <td className={masterTableData}>
                            {dept.deptName}                        
                          </td>
                          <td className={masterTableData}>
                            <span className={`inline-block px-2 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${
                              dept.status === 'Active' 
                                ? 'bg-emerald-100 text-emerald-700 border border-emerald-300' 
                                : 'bg-rose-100 text-rose-700 border border-rose-300'
                            }`}>
                              {dept.status || 'Active'}
                            </span>
                          </td>
                          <td className={masterTableData}>
                            <div className="flex justify-center gap-3">
                              <button onClick={() => handleEdit(dept)} className="rounded-md p-1 text-emerald-600 transition hover:bg-emerald-100">
                                <Edit size={14}/>
                              </button>
                              <button onClick={() => handleDelete(dept._id)} className="rounded-md p-1 text-rose-600 transition hover:bg-rose-100">
                                <Trash2 size={14}/>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="p-12 text-center text-slate-300 font-bold uppercase text-xs">No matching records found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default DepartmentList;